import React from 'react';
import { Resistor } from './primitives/Resistor';
import { VoltageSource } from './primitives/VoltageSource';
import { Wire } from './primitives/Wire';
import { Arrow } from './primitives/Arrow';
import { Junction } from './primitives/Junction';
import { Capacitor } from './primitives/Capacitor';
import { Inductor } from './primitives/Inductor';

export type Point = { x: number, y: number };

export interface CircuitElementBase {
    id?: string;
    from: Point;
    to: Point;
    label?: string;
    labelColor?: string;
    labelOffset?: number;
    labelSide?: 'left' | 'right'; // Control which side of the line the label appears
}

export interface WireElement extends CircuitElementBase {
    type: 'wire';
}

export interface ResistorElement extends CircuitElementBase {
    type: 'resistor';
    value?: number;
}

export interface VoltageSourceElement extends CircuitElementBase {
    type: 'voltageSource';
    value?: number;
}

export interface CurrentArrowElement extends CircuitElementBase {
    type: 'currentArrow';
}

export type CircuitElement = WireElement | ResistorElement | VoltageSourceElement | CurrentArrowElement | CapacitorElement | InductorElement;

export interface CapacitorElement extends CircuitElementBase {
    type: 'capacitor';
    value?: number;
}

export interface InductorElement extends CircuitElementBase {
    type: 'inductor';
    value?: number;
}

interface JunctionDefinition {
    x: number;
    y: number;
    label?: string;
    labelPosition?: 'top' | 'bottom' | 'left' | 'right' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
    color?: string;
    labelOffset?: number;
}

interface CircuitRendererProps {
    width: number;
    height: number;
    elements: CircuitElement[];
    junctions?: JunctionDefinition[];
}

export const CircuitRenderer: React.FC<CircuitRendererProps> = ({
    width,
    height,
    elements,
    junctions
}) => {
    return (
        <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            style={{ width: '100%', maxWidth: `${width}px`, height: 'auto', background: 'transparent' }}
        >
            {elements.map((el, i) => {
                const { from, to, type, label, labelColor, labelOffset, labelSide } = el;
                const dx = to.x - from.x;
                const dy = to.y - from.y;
                const length = Math.sqrt(dx * dx + dy * dy);
                const angleRad = Math.atan2(dy, dx);
                const angleDeg = angleRad * 180 / Math.PI;
                const midX = (from.x + to.x) / 2;
                const midY = (from.y + to.y) / 2;

                // Vector for direction
                const ux = dx / length || 0;
                const uy = dy / length || 0;

                // Normal vector (Standard: Right/Clockwise)
                // (0,1) rot -90 -> (1,0). 
                // dx=1, dy=0. ux=1, uy=0.
                // nx = -0 = 0. ny = 1. (Down). 
                // So Standard Normal is Right-Handed (relative to forward).
                let nx = -uy;
                let ny = ux;

                // If side is 'left', invert normal to be Left-Handed.
                if (labelSide === 'left') {
                    nx = -nx;
                    ny = -ny;
                }

                const effectiveLabelOffset = labelOffset ?? 30; // Default offset

                // Helper to calculate start/end for component gap
                const getGapPoints = (gapSize: number) => {
                    const halfGap = gapSize / 2;
                    const startGap = {
                        x: midX - ux * halfGap,
                        y: midY - uy * halfGap
                    };
                    const endGap = {
                        x: midX + ux * halfGap,
                        y: midY + uy * halfGap
                    };
                    return { startGap, endGap };
                };

                // Render Component
                let componentNode = null;

                if (type === 'wire') {
                    componentNode = <Wire x1={from.x} y1={from.y} x2={to.x} y2={to.y} />;
                }
                else if (type === 'resistor') {
                    const width = 60;
                    const { startGap, endGap } = getGapPoints(width);
                    componentNode = (
                        <g>
                            <Wire x1={from.x} y1={from.y} x2={startGap.x} y2={startGap.y} />
                            <Resistor
                                x={midX}
                                y={midY}
                                rotation={angleDeg}
                                value={(el as ResistorElement).value}
                            // We handle label externally now
                            />
                            <Wire x1={endGap.x} y1={endGap.y} x2={to.x} y2={to.y} />
                        </g>
                    );
                }
                else if (type === 'voltageSource') {
                    const size = 40;
                    const { startGap, endGap } = getGapPoints(size);
                    componentNode = (
                        <g>
                            <Wire x1={from.x} y1={from.y} x2={startGap.x} y2={startGap.y} />
                            <VoltageSource
                                x={midX}
                                y={midY}
                                rotation={angleDeg}
                                value={(el as VoltageSourceElement).value}
                                size={size}
                            />
                            <Wire x1={endGap.x} y1={endGap.y} x2={to.x} y2={to.y} />
                        </g>
                    );
                }
                else if (type === 'currentArrow') {
                    // Arrow handles label internally? 
                    // We should use Arrow primitive just for the graphic, and handle label here for consistency?
                    // Arrow primitive has logic to put label offset.
                    // But if we want unified 'labelSide' logic, we should probably handle it here.

                    // Let's pass 'label' to Arrow but set labelOffset based on our calculations?
                    // Or just draw Arrow graphic and draw Text here.
                    // Arrow primitive draws: Line + Polygon + Text.
                    // If we don't pass label to Arrow, it draws Line + Polygon.
                    // Then we draw Text.

                    componentNode = (
                        <Arrow
                            x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                            // No label passed
                            labelColor={labelColor}
                        />
                    );
                }
                else if (type === 'capacitor') {
                    const width = 50;
                    const { startGap, endGap } = getGapPoints(width);
                    componentNode = (
                        <g>
                            <Wire x1={from.x} y1={from.y} x2={startGap.x} y2={startGap.y} />
                            <Capacitor
                                x={midX}
                                y={midY}
                                rotation={angleDeg}
                                value={(el as CapacitorElement).value}
                            />
                            <Wire x1={endGap.x} y1={endGap.y} x2={to.x} y2={to.y} />
                        </g>
                    );
                }
                else if (type === 'inductor') {
                    const width = 60;
                    const { startGap, endGap } = getGapPoints(width);
                    componentNode = (
                        <g>
                            <Wire x1={from.x} y1={from.y} x2={startGap.x} y2={startGap.y} />
                            <Inductor
                                x={midX}
                                y={midY}
                                rotation={angleDeg}
                                value={(el as InductorElement).value}
                            />
                            <Wire x1={endGap.x} y1={endGap.y} x2={to.x} y2={to.y} />
                        </g>
                    );
                }

                // Determine label text
                let labelText = label;
                let showLabel = !!label;

                // Fallback: If no label is provided but value exists for Resistor/VoltageSource, use value
                if (!labelText) {
                    if (type === 'resistor' && (el as ResistorElement).value !== undefined) {
                        labelText = `${(el as ResistorElement).value}Ω`;
                        showLabel = true;
                    } else if (type === 'voltageSource' && (el as VoltageSourceElement).value !== undefined) {
                        labelText = `${(el as VoltageSourceElement).value}V`;
                        showLabel = true;
                    } else if (type === 'capacitor' && (el as CapacitorElement).value !== undefined) {
                        labelText = `${(el as CapacitorElement).value}F`;
                        showLabel = true;
                    } else if (type === 'inductor' && (el as InductorElement).value !== undefined) {
                        labelText = `${(el as InductorElement).value}H`;
                        showLabel = true;
                    }
                }

                // Render Label
                let labelNode = null;
                if (showLabel && labelText) {
                    const tx = midX + nx * effectiveLabelOffset;
                    const ty = midY + ny * effectiveLabelOffset;

                    // Determine if element is more vertical than horizontal
                    const isVertical = Math.abs(dy) > Math.abs(dx);

                    // For vertical elements, rotate the label to read along the element
                    // Only apply rotation for substantially vertical elements (>60 degrees from horizontal)
                    const shouldRotateLabel = isVertical && Math.abs(angleDeg) > 60 && Math.abs(angleDeg) < 120;

                    labelNode = (
                        <text
                            x={tx} y={ty}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fill={labelColor || "black"}
                            fontSize="14" fontWeight="500"
                            style={{ textShadow: '0px 0px 4px white' }}
                            transform={shouldRotateLabel ? `rotate(-90, ${tx}, ${ty})` : undefined}
                        >
                            {labelText}
                        </text>
                    );
                }

                return (
                    <g key={i}>
                        {componentNode}
                        {labelNode}
                    </g>
                );
            })}

            {junctions?.map((j, i) => (
                <Junction
                    key={`j-${i}`}
                    x={j.x}
                    y={j.y}
                    label={j.label}
                    labelPosition={j.labelPosition}
                    color={j.color}
                    labelOffset={j.labelOffset}
                />
            ))}

        </svg>
    );
};
